/**
 * businessInsightsClientsData.ts — Tarjetas demo de clientes.
 *
 * QUÉ HACE: declara las tarjetas 19–22 del explorador de Análisis del negocio
 *   (nuevos vs recurrentes, top clientes, frecuencia de compra y cohortes de
 *   retención) con datos DEMO deterministas para la pestaña de pruebas local.
 *
 * QUÉ INVOCA: helpers de `businessInsights.shared.ts`.
 * QUÉ EXPORTA: BUSINESS_INSIGHTS_CLIENTS (readonly BusinessInsightDataset[]).
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 */
import { Layers, UserPlus, Users } from "lucide-react";
import type { BusinessInsightDataset } from "../types/businessInsights.types";
import {
  INSIGHT_COLORS,
  INSIGHT_VIEW_PRESETS,
  insightRow,
} from "./businessInsights.shared";

const MONTHS = ["Abr", "May", "Jun", "Jul", "Ago", "Sep"] as const;
const NUEVOS = [84, 96, 78, 102, 91, 112];
const RECURRENTES = [320, 345, 361, 358, 402, 441];

/** Cohortes de retención (%), triangulares: cada cohorte solo tiene meses vividos. */
const COHORTES: Array<{ cohorte: string; valores: number[] }> = [
  { cohorte: "Ene", valores: [100, 62, 48, 41, 36, 32] },
  { cohorte: "Feb", valores: [100, 65, 52, 44, 38, 0] },
  { cohorte: "Mar", valores: [100, 60, 49, 42, 0, 0] },
  { cohorte: "Abr", valores: [100, 66, 54, 0, 0, 0] },
  { cohorte: "May", valores: [100, 64, 0, 0, 0, 0] },
  { cohorte: "Jun", valores: [100, 0, 0, 0, 0, 0] },
];

export const BUSINESS_INSIGHTS_CLIENTS: readonly BusinessInsightDataset[] = [
  {
    id: "clientes-nuevos-recurrentes",
    title: "Clientes nuevos vs recurrentes",
    subtitle: "Composición de la clientela — últimos 6 meses",
    icon: UserPlus,
    group: "clientes",
    valueKind: "integer",
    valueSuffix: " clientes",
    nameLabel: "Mes",
    series: [
      { key: "nuevos", label: "Nuevos", color: INSIGHT_COLORS.info },
      {
        key: "recurrentes",
        label: "Recurrentes",
        color: INSIGHT_COLORS.violeta,
      },
    ],
    rows: MONTHS.map((month, index) =>
      insightRow(month, {
        nuevos: NUEVOS[index],
        recurrentes: RECURRENTES[index],
      }),
    ),
    views: ["apiladas", ...INSIGHT_VIEW_PRESETS.temporal, "radar"],
    defaultView: "apiladas",
    badge: { mode: "total", label: "Clientes", tone: "blue" },
  },
  {
    id: "top-clientes",
    title: "Top clientes",
    subtitle: "Mayor valor comprado — trimestre",
    icon: Users,
    group: "clientes",
    valueKind: "currency",
    nameLabel: "Cliente",
    series: [{ key: "monto", label: "Comprado", color: INSIGHT_COLORS.exito }],
    rows: [
      insightRow("María Gómez", { monto: 4800000 }, "María Gómez — 12 compras"),
      insightRow("Andrés Ríos", { monto: 3900000 }, "Andrés Ríos — 8 compras"),
      insightRow(
        "Sofía Castro",
        { monto: 3200000 },
        "Sofía Castro — 15 compras",
      ),
      insightRow("Laura Pérez", { monto: 2800000 }, "Laura Pérez — 9 compras"),
      insightRow(
        "Carlos Muñoz",
        { monto: 2400000 },
        "Carlos Muñoz — 6 compras",
      ),
      insightRow(
        "Diana Torres",
        { monto: 2100000 },
        "Diana Torres — 11 compras",
      ),
      insightRow(
        "Jorge Ramírez",
        { monto: 1800000 },
        "Jorge Ramírez — 5 compras",
      ),
      insightRow(
        "Camila López",
        { monto: 1500000 },
        "Camila López — 7 compras",
      ),
      insightRow(
        "Felipe Vargas",
        { monto: 1200000 },
        "Felipe Vargas — 4 compras",
      ),
      insightRow("Ana Beltrán", { monto: 1000000 }, "Ana Beltrán — 6 compras"),
    ],
    views: INSIGHT_VIEW_PRESETS.rankingHorizontal,
    badge: { mode: "max", label: "Mejor cliente", tone: "green" },
  },
  {
    id: "cohortes-retencion",
    title: "Cohortes de retención",
    subtitle: "% de clientes que vuelve por mes",
    icon: Layers,
    group: "clientes",
    valueKind: "decimal",
    valueSuffix: "%",
    nameLabel: "Cohorte",
    series: ["Mes 0", "Mes 1", "Mes 2", "Mes 3", "Mes 4", "Mes 5"].map(
      (label, index) => ({
        key: `m${index}`,
        label,
        color: INSIGHT_COLORS.violeta,
      }),
    ),
    rows: COHORTES.map(({ cohorte, valores }) =>
      insightRow(
        cohorte,
        Object.fromEntries(valores.map((valor, index) => [`m${index}`, valor])),
      ),
    ),
    views: INSIGHT_VIEW_PRESETS.cohorte,
    badge: {
      mode: "average",
      label: "Retención mes 1",
      tone: "amber",
      seriesKey: "m1",
    },
    showTotal: false,
  },
];
